import React, { useEffect, useState } from "react";
import { api, UNIT_META } from "@/lib/api";
import { PageHeader, Panel, EmptyState } from "@/components/ui-parts";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DialogShell, Field, Input, Textarea, UnitSelect, ProductSelect,
} from "@/components/form-helpers";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function Production() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    unit: "",
    product: "",
    date: todayISO(),
    raw_input_kg: "",
    output_kg: "",
    wastage_kg: "",
    shift: "full",
    remarks: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/production");
      setRows(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/production", {
        ...form,
        raw_input_kg: Number(form.raw_input_kg) || 0,
        output_kg: Number(form.output_kg) || 0,
        wastage_kg: Number(form.wastage_kg) || 0,
      });
      toast.success("Production entry saved");
      setOpen(false);
      setForm({ unit: "", product: "", date: todayISO(), raw_input_kg: "", output_kg: "", wastage_kg: "", shift: "full", remarks: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Data Entry"
        title="Production Log"
        description="Record daily production output, input consumption and wastage per unit and product."
        testid="production-header"
        actions={
          <Button data-testid="add-production-btn" onClick={() => setOpen(true)} className="bg-amber-400 hover:bg-amber-300 text-black rounded-none">
            <Plus className="mr-2" size={14} weight="bold" /> New Entry
          </Button>
        }
      />

      <Panel title={`Recent Entries (${rows.length})`}>
        {loading ? (
          <div className="p-8 text-xs uppercase tracking-[0.3em] text-zinc-500">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState message="No production logged yet" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead className="label-caps">Date</TableHead>
                <TableHead className="label-caps">Unit</TableHead>
                <TableHead className="label-caps">Product</TableHead>
                <TableHead className="label-caps text-right">Input (kg)</TableHead>
                <TableHead className="label-caps text-right">Output (kg)</TableHead>
                <TableHead className="label-caps text-right">Wastage</TableHead>
                <TableHead className="label-caps text-right">Yield %</TableHead>
                <TableHead className="label-caps">Shift</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const yieldPct = r.raw_input_kg ? ((r.output_kg / r.raw_input_kg) * 100).toFixed(1) : "-";
                return (
                  <TableRow key={r.id} className="border-zinc-800" data-testid={`prod-row-${r.id}`}>
                    <TableCell className="font-mono text-xs">{r.date}</TableCell>
                    <TableCell><span className="label-caps" style={{ color: UNIT_META[r.unit]?.accent }}>{UNIT_META[r.unit]?.tag}</span></TableCell>
                    <TableCell>{r.product}</TableCell>
                    <TableCell className="text-right font-mono">{r.raw_input_kg}</TableCell>
                    <TableCell className="text-right font-mono text-amber-400">{r.output_kg}</TableCell>
                    <TableCell className="text-right font-mono">{r.wastage_kg}</TableCell>
                    <TableCell className="text-right font-mono">{yieldPct}%</TableCell>
                    <TableCell className="uppercase text-xs">{r.shift}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Panel>

      <DialogShell open={open} onOpenChange={setOpen} title="New Production Entry" onSubmit={submit} submitLabel="Save Entry" testidSubmit="save-production-btn">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Unit"><UnitSelect testid="prod-unit" value={form.unit} onChange={(v) => setForm({ ...form, unit: v, product: "" })} /></Field>
          <Field label="Product"><ProductSelect testid="prod-product" unit={form.unit} value={form.product} onChange={(v) => setForm({ ...form, product: v })} /></Field>
          <Field label="Date"><Input data-testid="prod-date" type="date" className="rounded-none bg-zinc-950 border-zinc-800" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
          <Field label="Shift">
            <Select value={form.shift} onValueChange={(v) => setForm({ ...form, shift: v })}>
              <SelectTrigger data-testid="prod-shift" className="rounded-none bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                <SelectItem value="full">Full Day</SelectItem>
                <SelectItem value="day">Day</SelectItem>
                <SelectItem value="night">Night</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Raw Input (kg)"><Input data-testid="prod-input" type="number" step="0.01" className="rounded-none bg-zinc-950 border-zinc-800" value={form.raw_input_kg} onChange={(e) => setForm({ ...form, raw_input_kg: e.target.value })} /></Field>
          <Field label="Output (kg)"><Input data-testid="prod-output" type="number" step="0.01" className="rounded-none bg-zinc-950 border-zinc-800" value={form.output_kg} onChange={(e) => setForm({ ...form, output_kg: e.target.value })} /></Field>
          <Field label="Wastage (kg)"><Input data-testid="prod-wastage" type="number" step="0.01" className="rounded-none bg-zinc-950 border-zinc-800" value={form.wastage_kg} onChange={(e) => setForm({ ...form, wastage_kg: e.target.value })} /></Field>
        </div>
        <Field label="Remarks"><Textarea data-testid="prod-remarks" className="rounded-none bg-zinc-950 border-zinc-800" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
      </DialogShell>
    </div>
  );
}
